import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { activityTracker } from '../services/activityTracker';

interface TimeTrackingState {
  isClockedIn: boolean;
  lastClockIn?: number;
  lastClockOut?: number;
  currentSessionDuration: number;
}

export const useTimeTracking = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [trackingState, setTrackingState] = useState<TimeTrackingState>({
    isClockedIn: false,
    currentSessionDuration: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Update current session duration for clocked in users
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (trackingState.isClockedIn && trackingState.lastClockIn) {
      interval = setInterval(() => {
        const now = Date.now();
        const duration = now - trackingState.lastClockIn!;
        setTrackingState(prev => ({
          ...prev,
          currentSessionDuration: duration
        }));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [trackingState.isClockedIn, trackingState.lastClockIn]);

  // Initialize tracking state on component mount
  useEffect(() => {
    if (user) {
      updateTrackingState();
      
      // Initialize activity tracker for managers (it will handle everything automatically)
      if (user.userType === 'manager') {
        activityTracker.init();
      }
    }
  }, [user]);

  // Handle page navigation for activity tracking
  useEffect(() => {
    if (user?.userType === 'manager') {
      activityTracker.handleNavigation(location.pathname);
    }
  }, [location.pathname, user]);

  const updateTrackingState = async () => {
    try {
      const status = await authService.getCurrentClockStatus();
      setTrackingState({
        isClockedIn: status.isClockedIn,
        lastClockIn: status.lastClockIn,
        lastClockOut: status.lastClockOut,
        currentSessionDuration: status.lastClockIn ? Date.now() - status.lastClockIn : 0
      });
    } catch (error) {
      console.error('Error updating tracking state:', error);
    }
  };

  const clockIn = async () => {
    if (!user || user.userType !== 'manager') {
      setError('Only managers can clock in');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const result = await authService.clockIn();
      
      if (result.success) {
        await updateTrackingState();
        // Activity tracker will automatically start tracking when clock status changes
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to clock in');
    }

    setIsLoading(false);
  };

  const clockOut = async () => {
    if (!user || user.userType !== 'manager') {
      setError('Only managers can clock out');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const result = await authService.clockOut();
      
      if (result.success) {
        await updateTrackingState();
        // Activity tracker will automatically stop tracking when clock status changes
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to clock out');
    }

    setIsLoading(false);
  };

  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
  };

  const formatDuration = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStatusDisplay = (): string => {
    if (trackingState.isClockedIn && trackingState.lastClockIn) {
      return `Clocked in at ${formatTime(trackingState.lastClockIn)}`;
    } else if (trackingState.lastClockOut) {
      return `Clocked out at ${formatTime(trackingState.lastClockOut)}`;
    }
    return 'Not clocked in';
  };

  return {
    trackingState,
    isLoading,
    error,
    clockIn,
    clockOut,
    formatTime,
    formatDuration,
    getStatusDisplay,
    canTrackTime: user?.userType === 'manager'
  };
}; 